// src/types/Light.ts
export interface LightPosition {
    x: number;
    y: number;
}

export interface LightProperties {
    color: string;
    intensity: number;
    radius: number;
}

export interface LightState {
    isOn: boolean;
    brightness: number;
    color: string;
}

export interface LightUpdate {
    name: string;
    isOn: boolean;
    brightness: number;
    color: string;
}

export interface LightDescription {
    name: string,
    groups: string[],
    description: string,
    maximum_lumens: number,
    position: LightPosition,
    current_state: LightState,
}

export interface Room {
    id: string;
    bounds: {
        x: number;
        y: number;
        width: number;
        height: number;
    };
    // Optional: Add path data if rooms aren't rectangular
    path?: Path2D;
}

export const DEFAULT_ROOMS: Room[] = [
    {
        id: 'office_study_den',
        bounds: { x: 0, y: 0, width: 282, height: 296 },
    },
    {
        id: 'bedroom',
        bounds: { x: 308, y: 0, width: 500, height: 296 },
    },
    {
        id: 'living_room',
        bounds: { x: 0, y: 322, width: 318, height: 600 },
    },
    {
        id: 'kitchen_dining_room',
        bounds: { x: 344, y: 322, width: 800, height: 600 },
    },
];

export const DEFAULT_LIGHTS: LightDescription[] = [
    {
        name: 'Desk Light',
        groups: ['office_study_den'],
        description: 'A desk light',
        maximum_lumens: 400,
        position: { x: 120, y: 280 },
        current_state: { isOn: false, brightness: 0.5, color: '#ffffff' },
    },
    {
        name: 'Night table Light',
        groups: ['bedroom'],
        description: 'Light next to the bed',
        maximum_lumens: 400,
        position: { x: 555, y: 280 },
        current_state: { isOn: false, brightness: 0.5, color: '#ffffff' },
    },
    {
        name: 'Bedroom Ceiling Fan',
        groups: ['bedroom'],
        description: 'Light in the ceiling fan over the bed.',
        maximum_lumens: 800,
        position: { x: 455, y: 180 },
        current_state: { isOn: false, brightness: 0.5, color: '#ffffff' },
    },
    {
        name: 'Floor lamp right side',
        groups: ['living_room'],
        description: 'In the right corner of the room, behind the TV',
        maximum_lumens: 800,
        position: { x: 290, y: 350 },
        current_state: { isOn: false, brightness: 0.5, color: '#ffffff' },
    },
    {
        name: 'Floor lamp left side',
        groups: ['living_room'],
        description: 'In the left corner of the room, behind the TV',
        maximum_lumens: 800,
        position: { x: 35, y: 350 },
        current_state: { isOn: false, brightness: 0.5, color: '#ffffff' },
    },
    {
        name: 'Dining Room Light',
        groups: ['kitchen_dining_room'],
        description: 'Over the dining room table',
        maximum_lumens: 800,
        position: { x: 440, y: 435 },
        current_state: { isOn: false, brightness: 0.5, color: '#ffffff' },
    },
    {
        name: 'Kitchen Counter Left',
        groups: ['kitchen_dining_room'],
        description: 'light over the sink',
        maximum_lumens: 400,
        position: { x: 620, y: 400 },
        current_state: { isOn: false, brightness: 0.5, color: '#ffffff' },
    },
    {
        name: 'Kitchen Counter Center',
        groups: ['kitchen_dining_room'],
        description: 'light over the counter',
        maximum_lumens: 400,
        position: { x: 620, y: 475 },
        current_state: { isOn: false, brightness: 0.5, color: '#ffffff' },
    },
    {
        name: 'Kitchen Counter Right',
        groups: ['kitchen_dining_room'],
        description: 'light over the dishwasher',
        maximum_lumens: 400,
        position: { x: 620, y: 550 },
        current_state: { isOn: false, brightness: 0.5, color: '#ffffff' },
    },
    {
        name: 'Office Floor Lamp',
        groups: ['office_study_den'],
        description: 'A floor light in the back corner of the room',
        maximum_lumens: 800,
        position: { x: 260, y: 120 },
        current_state: { isOn: false, brightness: 0.5, color: '#ffffff' },
    },
]

export class Light {
    position: LightPosition;
    properties: LightProperties;
    roomId: string;

    constructor(position: LightPosition, properties: LightProperties, roomId: string) {
        this.position = position;
        this.properties = properties;
        this.roomId = roomId;
    }

    static fromDescription(description: LightDescription): Light | undefined {

        const room = DEFAULT_ROOMS.find(room => description.groups.includes(room.id)) || DEFAULT_ROOMS[0]

        if (!description.current_state.isOn) {
            return undefined;
        }

        const radius = description.maximum_lumens / 1600 * 300;
        const intensity = description.current_state.brightness * description.maximum_lumens / 1600;

        return new Light(
            description.position,
            {
                color: description.current_state.color,
                intensity: intensity,
                radius: radius,
            },
            room.id
        );
    }
}